/**
 * Night Monitoring Settings Component
 * Settings UI for configuring night monitoring (EyeControl only)
 */

import React, { useState, useEffect } from 'react';
import { Moon, Mail, Clock, Timer } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { getUserSettings, saveUserSettings } from '@/lib/userSettings';
import { useCaregiverTranslation } from '@/hooks/useCaregiverTranslation';
import { cn } from '@/lib/utils';

export function NightMonitoringSettings() {
  const { t, dir } = useCaregiverTranslation();
  const [settings, setSettings] = useState(getUserSettings());
  
  const isEyeControlMode = settings.controlMethod === 'eye_control';
  
  const updateSetting = <K extends keyof typeof settings>(
    key: K,
    value: typeof settings[K]
  ) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    saveUserSettings(newSettings);
  };
  
  if (!isEyeControlMode) {
    return (
      <div className="p-4 bg-muted/30 rounded-xl border border-border" dir={dir}>
        <div className="flex items-center gap-3 text-muted-foreground">
          <Moon className="w-5 h-5" />
          <p className="text-sm">
            {(t as any)('nightMonitoringEyeControlOnly') || 'ניטור לילה זמין רק במצב אייקונטרול'}
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" dir={dir}>
      {/* Enable/Disable */}
      <div className="flex items-center justify-between p-4 bg-card rounded-xl border border-border">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Moon className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">
              {(t as any)('nightMonitoringTitle') || 'ניטור לילה'}
            </h3>
            <p className="text-sm text-muted-foreground">
              {(t as any)('nightMonitoringDesc') || 'התראה למטפל כשהמשתמש ער בלילה'}
            </p>
          </div>
        </div>
        <Switch
          checked={settings.nightMonitoringEnabled}
          onCheckedChange={(checked) => updateSetting('nightMonitoringEnabled', checked)}
        />
      </div>
      
      {settings.nightMonitoringEnabled && (
        <>
          {/* Caregiver Email */}
          <div className="p-4 bg-card rounded-xl border border-border space-y-3">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-primary" />
              <Label className="font-semibold">
                {(t as any)('nightCaregiverEmail') || 'אימייל המטפל/ת'}
              </Label>
            </div>
            <Input
              type="email"
              placeholder="caregiver@example.com"
              value={settings.caregiverEmail || ''}
              onChange={(e) => updateSetting('caregiverEmail', e.target.value || null)}
              className="text-base"
              dir="ltr"
            />
            <p className="text-xs text-muted-foreground">
              {(t as any)('nightCaregiverEmailDesc') || 'ההתראות יישלחו לכתובת זו'}
            </p>
          </div>
          
          {/* Time Range */}
          <div className="p-4 bg-card rounded-xl border border-border space-y-4">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary" />
              <Label className="font-semibold">
                {(t as any)('nightHoursRange') || 'טווח שעות לילה'}
              </Label>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">
                  {(t as any)('startTime') || 'שעת התחלה'}
                </Label>
                <Input
                  type="time"
                  value={settings.nightMonitoringStartTime}
                  onChange={(e) => updateSetting('nightMonitoringStartTime', e.target.value)}
                  className="text-center font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">
                  {(t as any)('endTime') || 'שעת סיום'}
                </Label>
                <Input
                  type="time"
                  value={settings.nightMonitoringEndTime}
                  onChange={(e) => updateSetting('nightMonitoringEndTime', e.target.value)}
                  className="text-center font-mono"
                />
              </div>
            </div>
          </div>
          
          {/* Awake Duration Threshold */}
          <div className="p-4 bg-card rounded-xl border border-border space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Timer className="w-4 h-4 text-primary" />
                <Label className="font-semibold">
                  {(t as any)('awakeDurationThreshold') || 'זמן ערות להתראה'}
                </Label>
              </div>
              <span className="text-lg font-bold text-primary">
                {settings.nightMonitoringDuration} {(t as any)('minutes') || 'דקות'}
              </span>
            </div>
            
            <Slider
              value={[settings.nightMonitoringDuration]}
              onValueChange={([value]) => updateSetting('nightMonitoringDuration', value)}
              min={2}
              max={30}
              step={1}
              className="w-full"
            />
            
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>2 {(t as any)('minutes') || 'דקות'}</span>
              <span>30 {(t as any)('minutes') || 'דקות'}</span>
            </div>
            
            <p className="text-xs text-muted-foreground">
              {(t as any)('awakeDurationDesc') || 'אם המשתמש ער ליותר מזמן זה, תישלח התראה'}
            </p>
          </div>
          
          {/* Info Box */}
          <div className="p-4 bg-primary/5 border border-primary/20 rounded-xl">
            <h4 className="font-semibold text-foreground mb-2">
              {(t as any)('howItWorks') || 'איך זה עובד?'}
            </h4>
            <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
              <li>{(t as any)('nightMonitoringInfo1') || 'כשהמשתמש ישן, האייקונטרול שולח סיגנל Enter רציף'}</li>
              <li>{(t as any)('nightMonitoringInfo2') || 'כשהסיגנל נפסק לזמן ממושך, המערכת מזהה ערות'}</li>
              <li>{(t as any)('nightMonitoringInfo3') || 'אחרי הזמן שהוגדר, נשלחת התראה למטפל'}</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
}